import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";

import AppCard from "@plannel/components/ui/AppCard";
import dateUtils, { DATETIME_MINUTE_FORMAT } from "@plannel/utils/date-util";
import { FS } from "@plannel/utils/font-util";

import AgendaStatusChip from "./AgendaStatusChip";
import AgendaPriorityChip from "./AgendaPriorityChip";
import AgendaTagChip from "./AgendaTagChip";
import AssigneeAvatars from "./AssigneeAvatars";

/**
 * 목록의 한 줄. 시안 ②(한 줄 카드)다.
 *
 * <p>카드 그리드와 같은 정보를 담되 요약을 한 줄로 줄이고 회의 시간을 오른쪽 전용 칸으로 빼서
 * 한 화면에 두 배 가까이 들어간다. 회의 시간이 세로로 줄 맞춰 보이는 것이 이 뷰의 요점이다 —
 * "다음 주에 뭐가 있지"를 훑기 좋다.
 */
const AgendaRow = ({ agenda, onOpen, onToggleStar, t }) => {
  const isDraft = agenda.statusCd === "DRAFT";

  return (
    <AppCard
      sx={{
        px: 2,
        py: 1.25,
        display: "flex",
        alignItems: "center",
        gap: 1.75,
        borderStyle: isDraft ? "dashed" : "solid",
        cursor: "pointer",
        "&:hover": { borderColor: "primary.main" }
      }}
      onClick={() => onOpen(agenda)}
    >
      <IconButton
        size="small"
        aria-label={t(agenda.starred ? "MSG_MeetingAgendaUnstar" : "MSG_MeetingAgendaStar")}
        onClick={(event) => {
          // 줄 전체가 클릭 대상이라 별을 누를 때 상세로 넘어가면 안 된다
          event.stopPropagation();
          onToggleStar(agenda);
        }}
      >
        {agenda.starred ? (
          <StarIcon fontSize="small" sx={{ color: "warning.main" }} />
        ) : (
          <StarBorderIcon fontSize="small" sx={{ color: "action.disabled" }} />
        )}
      </IconButton>

      <Box sx={{ width: 88, flexShrink: 0, display: "flex", justifyContent: "center" }}>
        <AgendaStatusChip statusCd={agenda.statusCd} t={t} />
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {/* 배지는 상태 칸(폭 고정)이 아니라 제목 앞에 둔다 — 저 칸에 넣으면 줄마다 폭이 달라진다 */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          <AgendaPriorityChip priorityCd={agenda.priorityCd} t={t} />
          <Typography
            sx={{ fontSize: FS.BODY, fontWeight: 600, color: "text.primary", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {agenda.title}
          </Typography>
        </Box>
        <Typography
          sx={{ fontSize: FS.CAPTION, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {isDraft ? t("MSG_MeetingAgendaDraftOnlyYou") : agenda.excerpt || ""}
        </Typography>
      </Box>

      {agenda.tags?.length > 0 && (
        <Box sx={{ flexShrink: 0, display: "flex", gap: 0.5 }}>
          {agenda.tags.slice(0, 2).map((tag) => (
            <AgendaTagChip key={tag} tag={tag} dense />
          ))}
        </Box>
      )}

      {/* 담당자. 폭을 고정해야 행마다 뒤 칸이 어긋나지 않는다 */}
      <Box sx={{ width: 84, flexShrink: 0, display: "flex", justifyContent: "flex-end" }}>
        <AssigneeAvatars assignees={agenda.assignees} max={3} size={22} />
      </Box>

      <Box sx={{ width: 132, flexShrink: 0, textAlign: "right" }}>
        <Typography
          sx={{
            fontSize: FS.CAPTION,
            fontWeight: agenda.meetingStartTs ? 600 : 400,
            color: agenda.meetingStartTs ? "text.primary" : "text.disabled",
            whiteSpace: "nowrap"
          }}
        >
          {agenda.meetingStartTs
            ? dateUtils.formatDate(agenda.meetingStartTs, DATETIME_MINUTE_FORMAT)
            : t("MSG_MeetingAgendaNoTime")}
        </Typography>
      </Box>

      <Box sx={{ width: 76, flexShrink: 0, display: "flex", justifyContent: "flex-end", gap: 1 }}>
        {agenda.attachmentCount > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            <AttachFileIcon sx={{ fontSize: 14, color: "text.disabled" }} />
            <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>{agenda.attachmentCount}</Typography>
          </Box>
        )}
        {agenda.commentCount > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 13, color: "text.disabled" }} />
            <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>{agenda.commentCount}</Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ width: 30, flexShrink: 0, display: "flex", justifyContent: "flex-end" }}>
        {agenda.createdUser && (
          <Tooltip title={agenda.createdUser}>
            <Avatar sx={{ width: 24, height: 24, fontSize: FS.CAPTION }}>
              {agenda.createdUser.trim().charAt(0).toUpperCase()}
            </Avatar>
          </Tooltip>
        )}
      </Box>
    </AppCard>
  );
};

export default AgendaRow;
